// SiteBlock (job page round): where the work is and how to get in: the full address with Directions, a
// small map of the street (drawn here, never fetched: the app puts its live map in this place), the note
// for whoever arrives ("Getting in": the gate code, the dog), and what's at the address (its equipment,
// its other jobs) as KeyValue, spread. Reads only --site-*; KeyValue and Link their own.
import type { ReactNode } from 'react';
import { KeyValue, type KeyValueItem } from '../KeyValue/KeyValue.tsx';
import { Link } from '../Link/Link.tsx';
import './SiteBlock.css';

export interface SiteBlockProps {
  /** "1184 Larch Way, Langford BC V9B 3K2". */
  address: string;
  onDirections?: () => void;
  directionsHref?: string;
  /** The note for whoever arrives. */
  gettingIn?: ReactNode;
  /** What's at the address: equipment, other jobs. */
  atAddress?: KeyValueItem[];
  /** Draw the street map (true). The app passes its own live map as `map` instead. */
  showMap?: boolean;
  map?: ReactNode;
  /** Getting in above the map (a record): the note is what someone arriving needs first. */
  isAccessFirst?: boolean;
}

/** A street map, drawn: three streets, the lot, a pin. It stands for the place, not its geography. */
function MapSketch({ address }: { address: string }) {
  return (
    <svg className="fl-site__map" viewBox="0 0 320 120" role="img" aria-label={`Map of ${address}`}>
      <path className="fl-site__street" d="M0 38h320M0 96h320M120 0v120M252 0v120" />
      <path className="fl-site__lot" d="M150 52h60v32h-60z" />
      <path className="fl-site__pin" d="M180 66s-9-8-9-14a9 9 0 0 1 18 0c0 6-9 14-9 14z" />
      <circle className="fl-site__pin-dot" cx="180" cy="52" r="3" />
    </svg>
  );
}

export function SiteBlock({
  address,
  onDirections,
  directionsHref,
  gettingIn,
  atAddress,
  showMap = true,
  map,
  isAccessFirst = false,
}: SiteBlockProps) {
  const access = gettingIn ? (
    <div className="fl-site__in">
      <p className="fl-site__in-head">Getting in</p>
      <div className="fl-site__in-text">{gettingIn}</div>
    </div>
  ) : null;
  return (
    <div className="fl-site">
      <p className="fl-site__address">
        {address}
        {onDirections || directionsHref ? (
          <>
            {' · '}
            <Link href={directionsHref} onPress={onDirections}>
              Directions
            </Link>
          </>
        ) : null}
      </p>
      {isAccessFirst ? access : null}
      {map ?? (showMap ? <MapSketch address={address} /> : null)}
      {isAccessFirst ? null : access}
      {atAddress?.length ? (
        <div className="fl-site__at">
          <p className="fl-site__sub">At this address</p>
          <KeyValue items={atAddress} layout="spread" label="At this address" />
        </div>
      ) : null}
    </div>
  );
}
